import { useEffect } from "react";
import { z } from "zod";
import { useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGeolocation } from "@uidotdev/usehooks";

import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { db } from "@/stores/indexed-db";
import { useUserValue } from "@/stores/user-store";

import {
  metadataSchema,
  farmerInfoSchema,
  farmInfoSchema,
} from "@/doctypes/outgrower_monitoring_questionnaire.zod";

// Combined schema (exclude first_name, last_name, cluster, no_of_hectares)
const combinedSchema = metadataSchema
  .merge(farmerInfoSchema.pick({ farmer: true }))
  .merge(farmInfoSchema.omit({ no_of_hectares: true }))
  .superRefine((data, ctx) => {
    if (data.crop_condition === "Bad" && (!data.remarks || data.remarks.trim() === "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["remarks"],
        message: "Remarks are required when Crop Condition is Bad",
      });
    }
    if (
      data.are_you_satisfied_with_the_quality_of_input_disbursed === "No" &&
      (!data.what_would_you_like_improvement_on || data.what_would_you_like_improvement_on.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["what_would_you_like_improvement_on"],
        message: "This field is required when the farmer is not satisfied",
      });
    }
  });

// Single combined form
const CombinedForm = () => {
  const { register, formState: { errors }, setValue, getValues } = useFormContext<z.infer<typeof combinedSchema>>();
  const name = useUserValue("name");
  const email = useUserValue("email");
  const location = useGeolocation({ enableHighAccuracy: true, timeout: 5000 });

  // Prefill field officer once
  useEffect(() => {
    const existing = getValues("field_officer");
    if (!existing) {
      const fo = name === "Administrator" ? name : email;
      if (fo) setValue("field_officer", fo);
    }
  }, [name, email]);

  // Prefill lat/long when available
  useEffect(() => {
    if (location) {
      const lon = location.longitude ? Number(location.longitude).toPrecision(6) : undefined;
      const lat = location.latitude ? Number(location.latitude).toPrecision(6) : undefined;
      if (lon) setValue("longitude", String(lon));
      if (lat) setValue("latitude", String(lat));
    }
  }, [location]);

  const PACKAGES = ["Maize", "Sorghum/Soya"] as const;
  const CONDITIONS = ["Good", "Bad"] as const;
  const YES_NO = ["Yes", "No"] as const;

  return (
    <div className="space-y-4 text-start">
      {/* Metadata */}
      <div className="space-y-2">
        <Label htmlFor={register("farm_visit_date").name}>Farm Visit Date</Label>
        <Input
          id={register("farm_visit_date").name}
          type="date"
          className="block w-full rounded-md border p-2"
          {...register("farm_visit_date")}
        />
        {errors.farm_visit_date && <p className="text-sm text-destructive">{errors.farm_visit_date.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor={register("field_officer").name}>Field Officer</Label>
        <Input id={register("field_officer").name} className="block w-full rounded-md border p-2" {...register("field_officer")} />
        {errors.field_officer && <p className="text-sm text-destructive">{errors.field_officer.message}</p>}
      </div>

      {/* Farmer Info (reduced) */}
      <div className="space-y-2">
        <Label htmlFor={register("farmer").name}>Farmer</Label>
        <Input id={register("farmer").name} className="block w-full rounded-md border p-2" placeholder="Use the farmer's phone number" {...register("farmer")} />
        {errors.farmer && <p className="text-sm text-destructive">{errors.farmer.message}</p>}
      </div>

      {/* Farm Info (reduced) */}
      <div className="space-y-2">
        <Label htmlFor={register("latitude").name}>Latitude</Label>
        <Input id={register("latitude").name} className="block w-full rounded-md border p-2" {...register("latitude")} />
        {errors.latitude && <p className="text-sm text-destructive">{errors.latitude.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor={register("longitude").name}>Longitude</Label>
        <Input id={register("longitude").name} className="block w-full rounded-md border p-2" {...register("longitude")} />
        {errors.longitude && <p className="text-sm text-destructive">{errors.longitude.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor={register("farm_photo").name}>Farm Photo</Label>
        <Input id={register("farm_photo").name} type="file" className="block w-full rounded-md border px-2" {...register("farm_photo")} />
        {errors.farm_photo && <p className="text-sm text-destructive">{(errors.farm_photo as any).message as string}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor={register("package").name}>Package</Label>
        <Select onValueChange={(value: typeof PACKAGES[number]) => setValue("package", value)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Package" />
          </SelectTrigger>
          <SelectContent>
            {PACKAGES.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.package && <p className="text-sm text-destructive">{errors.package.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor={register("crop_condition").name}>Crop Condition</Label>
        <Select onValueChange={(value: typeof CONDITIONS[number]) => setValue("crop_condition", value)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Condition" />
          </SelectTrigger>
          <SelectContent>
            {CONDITIONS.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.crop_condition && <p className="text-sm text-destructive">{errors.crop_condition.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor={register("remarks").name}>Remarks</Label>
        <Input id={register("remarks").name} className="block w-full rounded-md border p-2" {...register("remarks")} />
        {errors.remarks && <p className="text-sm text-destructive">{errors.remarks.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor={register("are_you_satisfied_with_the_quality_of_input_disbursed").name}>
          Are you satisfied with the quality of input disbursed?
        </Label>
        <Select onValueChange={(value: typeof YES_NO[number]) => setValue("are_you_satisfied_with_the_quality_of_input_disbursed", value)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Yes/No" />
          </SelectTrigger>
          <SelectContent>
            {YES_NO.map((v) => (
              <SelectItem key={v} value={v}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.are_you_satisfied_with_the_quality_of_input_disbursed && (
          <p className="text-sm text-destructive">{errors.are_you_satisfied_with_the_quality_of_input_disbursed.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={register("what_would_you_like_improvement_on").name}>What would you like improvement on?</Label>
        <Input id={register("what_would_you_like_improvement_on").name} className="block w-full rounded-md border p-2" {...register("what_would_you_like_improvement_on")} />
        {errors.what_would_you_like_improvement_on && <p className="text-sm text-destructive">{errors.what_would_you_like_improvement_on.message}</p>}
      </div>
    </div>
  );
};

export const OutgrowerMonitoringQuestionnaireForm = () => {
  const form = useForm<z.infer<typeof combinedSchema>>({
    mode: "onBlur",
    resolver: zodResolver(combinedSchema),
    defaultValues: {
      farm_visit_date: new Date().toISOString().slice(0, 10),
    } as any,
  });

  const onSubmit = async (data: z.infer<typeof combinedSchema>) => {
    try {
      const farm_visit_date = form.getValues("farm_visit_date") as string | undefined;
      const field_officer = form.getValues("field_officer") as string | undefined;

      const id = `${farm_visit_date ?? new Date().toISOString().slice(0, 10)}_${field_officer ?? "unknown"}_${Date.now()}`;

      const existing = await db.outgrower_monitoring_questionnaires.get(id);
      if (existing) {
        toast.error("A questionnaire with the same ID already exists. Please try again.");
        return;
      }

      await db.outgrower_monitoring_questionnaires.add({
        id,
        ...(data as any),
        status: "unsynced",
        creation: new Date().toISOString(),
        modified: new Date().toISOString(),
      });

      toast.success("Outgrower monitoring questionnaire submitted");
      form.reset({ farm_visit_date: new Date().toISOString().slice(0, 10) } as any);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save questionnaire locally. Please try again.");
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <CombinedForm />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="submit">Submit</Button>
        </div>
      </form>
    </Form>
  );
};
